import { NgModule, LOCALE_ID } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MAT_DATE_FORMATS, DateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';

import { RoundProgressModule } from 'angular-svg-round-progressbar';

import { UiIconModule } from '@cigna/ui/icon';
import { UiButtonModule } from '@cigna/ui/button';
import { UiInputModule } from '@cigna/ui/input';
import { AnalyticsUiModule } from '@cigna/analytics/ui';
import { UiProgressModule } from '@cigna/ui/progress';
import { UiCollapsibleModule } from '@cigna/ui/collapsible';
import { UiAlertModule } from '@cigna/ui/alert';

import { FlagComponent } from './components/flag/flag.component';
import { HistoryUiComponent } from './components/history-ui/history-ui.component';
import { BarGraphComponent } from './components/bar-graph/bar-graph.component';
import { ProcessStepsComponent } from './components/process-steps/process-steps.component';
import { ResizeableHeightContainerComponent } from './components/resizeable-height-container/resizeable-height-container.component';
import { FilterTextInputComponent } from './components/filter-text-input/filter-text-input.component';
import { LoginComponent } from './components/login/login.component';
import { PasswordHintsComponent } from './components/password-hints/password-hints.component';
import { DatatableActionLinksComponent } from './components/datatable-action-links/datatable-action-links.component';
import { SelectTinsGroupComponent } from './components/select-tins-group/select-tins-group.component';
import { DialogAppealAttachHelpComponent } from './components/dialog-appeal-attach-help/dialog-appeal-attach-help.component';
import {
  MaskTextPipe,
  PhoneNumberPipe,
  RestrictedTextPipe,
  FormatBytesPipe,
  HighlightPipe,
  StateNamePipe,
  SafePipe,
  ObfuscatePipe,
} from './pipes';

import {
  TrimInputWhitespaceDirective,
  HoverDropdownDirective,
} from './directives';

import {
  CignaDateAdapterModule,
  CIGNA_DATE_FORMATS,
} from './modules/cigna-date-adapter.module';

import { ChcpSharedEnvironmentUtilModule } from '@cigna/chcp/shared/environment-util';

import { LobPreferenceDialogComponent } from './components/lob-preference-dialog/lob-preference-dialog.component';
import { FileUploadComponent } from './components/file-upload/file-upload.component';
import { DndFilesDirective } from './components/file-upload-dnd/dnd-files.directive';
import { FileUploadListComponent } from './components/file-upload-list/file-upload-list.component';
import { FileUploadDndComponent } from './components/file-upload-dnd/file-upload-dnd.component';
import { AttachmentsListComponent } from './components/attachments-list/attachments-list.component';
import { ReconsiderationHistoryComponent } from './components/reconsideration-history/reconsideration-history.component';
import { RoundGraphComponent } from './components/round-graph/round-graph.component';
import { PrecertStatusComponent } from './components/precert-status/precert-status.component';
import { ClaimStatusComponent } from './components/claim-status/claim-status.component';
import { CoverageStatusComponent } from './components/coverage-status/coverage-status.component';
import { BackToTopComponent } from './components/back-to-top/back-to-top.component';
import { FiltersBarSharedComponent } from './components/filters-bar/filters-bar.component';
import { FilterLabelSharedComponent } from './components/filter-label/filter-label.component';
import { FilterMultiselectSharedComponent } from './components/filter-multiselect/filter-multiselect.component';
import { FilterTypeDateSharedComponent } from './components/filter-type-date/filter-type-date.component';
import { TrimInputCurrencyDirective } from './directives/trim-input-currency.directive';
import { DialogVideoEmbedComponent } from './components/dialog-video-embed/dialog-video-embed.component';
import { MessageDeleteDialogComponent } from './components/message-delete-dialog/message-delete-dialog.component';
import { DialogUpdatePrefLobComponent } from './components/dialog-update-pref-lob/dialog-update-pref-lob.component';
import { ReactiveComponentModule } from '@ngrx/component';

@NgModule({
  imports: [
    CommonModule,
    UiIconModule,
    UiInputModule,
    UiButtonModule,
    UiCollapsibleModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    RoundProgressModule,
    AnalyticsUiModule,
    MatDialogModule,
    UiProgressModule,
    TranslateModule,
    UiAlertModule,
    FormsModule,
    ChcpSharedEnvironmentUtilModule,
    ReactiveComponentModule,
  ],
  declarations: [
    FlagComponent,
    HistoryUiComponent,
    ProcessStepsComponent,
    ResizeableHeightContainerComponent,
    FilterTextInputComponent,
    DatatableActionLinksComponent,
    LoginComponent,
    PasswordHintsComponent,
    BarGraphComponent,
    MaskTextPipe,
    FormatBytesPipe,
    PhoneNumberPipe,
    RestrictedTextPipe,
    HighlightPipe,
    ObfuscatePipe,
    TrimInputWhitespaceDirective,
    TrimInputCurrencyDirective,
    HoverDropdownDirective,
    DndFilesDirective,
    FileUploadComponent,
    FileUploadListComponent,
    FileUploadDndComponent,
    AttachmentsListComponent,
    ClaimStatusComponent,
    CoverageStatusComponent,
    BackToTopComponent,
    RoundGraphComponent,
    ReconsiderationHistoryComponent,
    PrecertStatusComponent,
    StateNamePipe,
    SafePipe,
    SelectTinsGroupComponent,
    DialogAppealAttachHelpComponent,
    FiltersBarSharedComponent,
    FilterLabelSharedComponent,
    FilterMultiselectSharedComponent,
    FilterTypeDateSharedComponent,
    LobPreferenceDialogComponent,
    DialogVideoEmbedComponent,
    MessageDeleteDialogComponent,
    DialogUpdatePrefLobComponent,
  ],
  exports: [
    MatDatepickerModule,
    FlagComponent,
    HistoryUiComponent,
    ProcessStepsComponent,
    ResizeableHeightContainerComponent,
    FilterTextInputComponent,
    DatatableActionLinksComponent,
    LoginComponent,
    PasswordHintsComponent,
    BarGraphComponent,
    MaskTextPipe,
    FormatBytesPipe,
    PhoneNumberPipe,
    RestrictedTextPipe,
    HighlightPipe,
    ObfuscatePipe,
    TrimInputWhitespaceDirective,
    TrimInputCurrencyDirective,
    HoverDropdownDirective,
    DndFilesDirective,
    FileUploadComponent,
    AttachmentsListComponent,
    ClaimStatusComponent,
    CoverageStatusComponent,
    BackToTopComponent,
    RoundGraphComponent,
    ReconsiderationHistoryComponent,
    PrecertStatusComponent,
    StateNamePipe,
    SafePipe,
    SelectTinsGroupComponent,
    DialogAppealAttachHelpComponent,
    FiltersBarSharedComponent,
    FilterLabelSharedComponent,
    FilterMultiselectSharedComponent,
    FilterTypeDateSharedComponent,
    LobPreferenceDialogComponent,
    MessageDeleteDialogComponent,
    DialogUpdatePrefLobComponent,
  ],
  providers: [
    { provide: LOCALE_ID, useValue: 'en-US' },
    {
      provide: DateAdapter,
      useClass: CignaDateAdapterModule,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: CIGNA_DATE_FORMATS,
    },
    PhoneNumberPipe,
    DatePipe,
  ],
})
export class ChcpSharedUiModule {}
